import { Box, Paper, Tab, Tabs, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import type { ITabConfig } from './ITabConfig';

/**
 * Modern tab container component using MUI Tabs.
 *
 * @param {object} props - The component props.
 * @param {ITabConfig[]} props.tabs - An array of tab configurations.
 * @returns {JSX.Element} - The rendered tab container.
 */
export function MvpTabContainer({ tabs, showIcons = true }: { tabs: ITabConfig[]; showIcons?: boolean }) {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Find the active tab index
    const activeTabIndex = tabs.findIndex((tab) => tab.link === location.pathname);
    const [tabIndex, setTabIndex] = useState(activeTabIndex >= 0 ? activeTabIndex : 0);

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTabIndex(newValue);
        navigate(tabs[newValue].link);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    width: '100%',
                    maxWidth: {
                        xs: 'calc(100% - 4px)', // Mobile: fast volle Breite
                        sm: 'calc(100% - 30px)', // Tablet: wie contentOuter mit margin-left/right 15px
                        md: '1200px', // Desktop: maximale Breite
                        xl: '1600px', // XL: maximale Breite
                    },
                    display: 'flex',
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0,
                    borderBottomLeftRadius: 12,
                    borderBottomRightRadius: 12,
                    borderLeft: `1px solid ${theme.palette.divider}`,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    borderRight: `1px solid ${theme.palette.divider}`,
                    overflow: 'hidden',
                }}
            >
                <Tabs
                    value={tabIndex}
                    onChange={handleTabChange}
                    variant={isMobile ? 'scrollable' : tabs.length < 4 ? 'standard' : 'fullWidth'}
                    scrollButtons={isMobile ? 'auto' : false}
                    allowScrollButtonsMobile={isMobile}
                    indicatorColor="primary"
                    textColor="primary"
                    sx={{
                        width: '100%',
                        '& .MuiTab-root': {
                            fontWeight: 400,
                            '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.2),
                            },
                            '&.Mui-selected': {
                                bgcolor: alpha(theme.palette.primary.main, 0.2),
                                fontWeight: 500,
                            },
                        },
                    }}
                >
                    {tabs.map((tab) => (
                        <Tab key={tab.link} label={tab.title} icon={showIcons ? tab.icon : undefined} />
                    ))}
                </Tabs>
            </Paper>
        </Box>
    );
}
